import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function MaterialiBlock({ cid, commessaId, categorie, initialMateriali, onSaved }) {
  const [rows, setRows] = useState(initialMateriali || []);
  const [prodotti, setProdotti] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRows(initialMateriali || []); }, [initialMateriali]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/prodotti?commessa_id=${commessaId}`);
        setProdotti(data);
      } catch { /* ignore */ }
    })();
  }, [commessaId]);

  const prodById = useMemo(() => Object.fromEntries(prodotti.map((p) => [p.id, p])), [prodotti]);

  const addRow = (categoria) => {
    setRows([...rows, { categoria, prodotto_id: "", quantita: 1, scarica_da: "nessuno" }]);
  };
  const updateRow = (i, patch) => {
    const copy = [...rows];
    copy[i] = { ...copy[i], ...patch };
    setRows(copy);
  };
  const removeRow = (i) => {
    setRows(rows.filter((_, j) => j !== i));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows
        .filter((r) => r.prodotto_id)
        .map((r) => ({
          categoria: r.categoria,
          prodotto_id: r.prodotto_id,
          quantita: Number(r.quantita) || 1,
          scarica_da: r.scarica_da || "nessuno",
        }));
      const { data } = await api.put(`/clienti/${cid}/installazione/materiali`, payload);
      if (data.scarichi_saltati?.length > 0) {
        const msg = data.scarichi_saltati
          .map((s) => `${s.prodotto} (${s.magazzino}): ${s.disponibili}/${s.richiesti}`)
          .join(" · ");
        toast.warning(`Scorta insufficiente per ${data.scarichi_saltati.length} articoli: ${msg}`, { duration: 8000 });
      }
      if (data.scarichi_auto > 0) toast.success(`Materiali salvati · ${data.scarichi_auto} scarichi applicati al magazzino`);
      else toast.success("Materiali salvati");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const subtotal = (cat) => rows
    .filter((r) => r.categoria === cat && r.prodotto_id)
    .reduce((sum, r) => sum + (Number(r.quantita) || 0) * Number(prodById[r.prodotto_id]?.costo_unitario || 0), 0);

  const total = rows.reduce(
    (sum, r) => sum + (Number(r.quantita) || 0) * Number(prodById[r.prodotto_id]?.costo_unitario || 0),
    0,
  );

  return (
    <div className="card-shell p-5" data-testid="materiali-block">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Materiali installati</h3>
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-materiali">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva materiali
        </button>
      </div>

      {prodotti.length === 0 && (
        <div className="mb-3 p-3 rounded-md text-xs" style={{ background: "#FFF4E5", color: "#8A4D0F" }}>
          Nessun prodotto associato a questa commessa. Vai in Prodotti e associa prodotti alla commessa corrente.
        </div>
      )}

      <div className="space-y-4">
        {categorie.map((cat) => {
          const catRows = rows.map((r, i) => ({ ...r, _i: i })).filter((r) => r.categoria === cat.slug);
          const availProds = prodotti.filter((p) => !p.categoria || p.categoria === cat.slug);
          return (
            <div key={cat.slug} className="border rounded-lg" style={{ borderColor: "var(--border)" }} data-testid={`mat-cat-${cat.slug}`}>
              <div className="px-4 py-2 flex justify-between items-center" style={{ background: "var(--surface-muted)" }}>
                <div className="font-semibold text-sm">{cat.label}</div>
                <div className="flex items-center gap-3">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Subtotale: </span>
                  <span className="font-bold text-sm" style={{ color: "var(--primary)" }}>€ {subtotal(cat.slug).toFixed(2)}</span>
                  <button onClick={() => addRow(cat.slug)} className="btn-secondary text-xs py-1 px-2">
                    <Plus className="w-3.5 h-3.5" /> Aggiungi
                  </button>
                </div>
              </div>
              {catRows.length > 0 && (
                <div className="p-3 space-y-2">
                  {catRows.map((r) => {
                    const p = prodById[r.prodotto_id];
                    const cu = Number(p?.costo_unitario || 0);
                    const tot = cu * (Number(r.quantita) || 0);
                    return (
                      <div key={r._i} className="grid grid-cols-12 gap-2 items-center text-sm">
                        <select
                          value={r.prodotto_id}
                          onChange={(e) => updateRow(r._i, { prodotto_id: e.target.value })}
                          className="col-span-4 px-2 py-1.5 rounded-md border text-sm outline-none"
                          style={{ borderColor: "var(--border)", background: "#fff" }}
                          data-testid={`mat-prod-${r._i}`}
                        >
                          <option value="">Seleziona prodotto…</option>
                          {availProds.map((pp) => (
                            <option key={pp.id} value={pp.id}>{pp.nome} {pp.sku ? `(${pp.sku})` : ""}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={r.quantita}
                          onChange={(e) => updateRow(r._i, { quantita: e.target.value })}
                          className="col-span-1 px-2 py-1.5 rounded-md border text-sm outline-none"
                          style={{ borderColor: "var(--border)", background: "#fff" }}
                          data-testid={`mat-qty-${r._i}`}
                        />
                        <select
                          value={r.scarica_da || "nessuno"}
                          onChange={(e) => updateRow(r._i, { scarica_da: e.target.value })}
                          className="col-span-2 px-2 py-1.5 rounded-md border text-xs outline-none"
                          style={{ borderColor: "var(--border)", background: "#fff" }}
                          data-testid={`mat-scarico-${r._i}`}
                          title="Scarica dal magazzino"
                        >
                          <option value="nessuno">No scarico</option>
                          <option value="virtuale">Virtuale</option>
                          <option value="fisico">Fisico</option>
                        </select>
                        <div className="col-span-1 text-xs" style={{ color: "var(--text-muted)" }}>€ {cu.toFixed(2)}</div>
                        <div className="col-span-3 font-semibold text-right">€ {tot.toFixed(2)}</div>
                        <button onClick={() => removeRow(r._i)} className="col-span-1 p-1.5 rounded hover:bg-black/5 justify-self-end">
                          <Trash2 className="w-4 h-4" style={{ color: "#B30000" }} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
        <div className="text-sm font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Totale materiali</div>
        <div className="text-3xl font-extrabold" style={{ color: "var(--primary)", fontFamily: "Manrope" }}>€ {total.toFixed(2)}</div>
      </div>
    </div>
  );
}
